import { useEffect, useMemo, type ReactNode } from "react";
import { matchRoutes, renderMatches, Outlet, useLocation } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { SHELL_ROUTES } from "../../router";
import { useTabStore } from "../../lib/tabStore";
import { TabActiveProvider } from "../../lib/tabActive";
import "./TabViews.css";

/**
 * SEKME GÖVDELERİ — açık her sekme AYNI ANDA mount, yalnız aktif olan görünür.
 *
 * ── Neden hepsi mount ──
 * Özelliğin tek amacı "başka sayfaya bakmak için işi yarım bırakmamak". Pasif sekme
 * unmount edilseydi yarım doldurulmuş form, seçimler ve kaydırma konumu silinirdi —
 * yani çözülmek istenen sorunun ta kendisi kalırdı.
 *
 * ── HER sekme AYNI yolla render edilir (aktif olan dahil) ──
 * Aktif sekmeyi `<Outlet/>`, pasifleri `renderMatches` ile basmak CAZİP ama YANLIŞTI:
 * sekme aktiflik değiştirdiğinde çocuk bileşenin TİPİ değişir, React eski ağacı söker ve
 * yerine yenisini kurar — yani sekme değiştirmek durumu tam olarak silerdi. Bu yüzden
 * aktiflik SADECE `hidden` özniteliğini değiştirir; ağacın kimliği hiç değişmez.
 *
 * ── Gizleme yöntemi: `hidden` ──
 * `display: none` uygulanır; React ağacı ve state YAŞAR, yalnız düzen/boyama durur.
 * `visibility`/`opacity` KULLANILMAZ: onlar elemanları Tab sırasında ve ekran okuyucuda
 * bırakır, ayrıca sabit konumlu toast yığınları aktif sekmenin üstüne binerdi.
 *
 * ── `renderMatches` güvenli mi ──
 * Rotalarda `loader`/`action` YOK (veri-router özellikleri kullanılmıyor), bu yüzden
 * `renderMatches`in veri-router bağlamı kurmaması bir kayıp değil. `useParams` doğru
 * çalışır (RouteContext kurulur); `useNavigate`/`useLocation`/`useSearchParams` global
 * RouterProvider bağlamından gelir.
 */
export function TabViews() {
  const tabs = useTabStore((s) => s.tabs);
  const activeId = useTabStore((s) => s.activeId);
  // `gen` tesis değişiminde artar → tüm paneller yeniden mount olur, eski tesise ait
  // seçimler (lokasyon, lot, merkez) sekmelerde ASILI KALMAZ.
  const gen = useTabStore((s) => s.gen);
  const { pathname } = useLocation();
  const queryClient = useQueryClient();

  /**
   * SEKMEYE DÖNÜNCE VERİYİ TAZELE.
   *
   * Sekmelerden ÖNCE her gezinme sayfayı yeniden mount ediyordu ve React Query'nin
   * `refetchOnMount` davranışı listeyi kendiliğinden tazeliyordu. Keep-alive ile sayfa
   * mount KALDIĞI için o tazeleme hiç tetiklenmiyor: başka bir tarayıcı sekmesinde ya da
   * başka bir kullanıcı tarafından yapılan değişiklik, siz sekmeye dönseniz bile listede
   * GÖRÜNMÜYORDU — tek çare sayfayı yeniden yüklemekti. Bu, sekme özelliğinin getirdiği
   * bir gerilemeydi.
   *
   * `stale: true` ŞART: `staleTime` 30 sn olduğundan hızlı sekme geçişleri hiçbir istek
   * üretmez; yalnız gerçekten bayatlamış sorgular tazelenir. Ölçüm: 10 sekme açıkken
   * tam tazeleme 31 yordam / 4 istek (istemci parti sınırı sayesinde).
   *
   * NOT: Aynı sekmedeki bir mutasyonun sonucu zaten anında yansır — `invalidateQueries`
   * tüm mount sekmelere ulaşır. Bu efekt DIŞARIDAN gelen değişiklikler içindir.
   */
  useEffect(() => {
    if (!activeId) return;
    void queryClient.refetchQueries({ type: "active", stale: true });
  }, [activeId, queryClient]);

  // Sekme listesi henüz kurulmadıysa (ilk render) normal Outlet davranışı.
  if (tabs.length === 0) {
    return <main className="shell-main"><Outlet /></main>;
  }

  /**
   * SEKMESİ OLMAYAN KONUM = YÖNLENDİRME ROTASI (`/formlar`, `/items`, `/parties`, `/`).
   * Bunlar sekme açmaz; ama yönlendirmeyi yapan bileşen (`FormsIndexRedirect`, `<Navigate>`)
   * BİR YERDE mount olmak zorunda — olmazsa adres o yolda TAKILIR ve ekranda önceki sekme
   * kalır. Gizli bir kapta `<Outlet/>` render edilir: bileşen mount olur, yönlendirmesini
   * yapar, hedef sekme açılır ve bu kap kaybolur. Koşullu yönlendirmeler de (süper-admin
   * Form Panosu'na, diğerleri Form Listesi'ne gider) böylece kendi mantığıyla çalışır —
   * statik bir "yol → hedef" tablosu bunu yapamazdı.
   */
  const konumunSekmesiVar = tabs.some((t) => t.path === pathname);

  return (
    <main className="shell-main shell-main-tabs">
      {tabs.map((t) => (
        <TabPanel key={`${t.id}#${gen}`} path={t.path} active={t.id === activeId}>
          <TabRoute path={t.path} />
        </TabPanel>
      ))}
      {!konumunSekmesiVar ? (
        <div className="tab-panel" hidden data-tab-path={`(yönlendirme) ${pathname}`}>
          <Outlet />
        </div>
      ) : null}
    </main>
  );
}

function TabPanel({ path, active, children }: { path: string; active: boolean; children: ReactNode }) {
  return (
    <div
      className="tab-panel"
      hidden={!active}
      data-tab-path={path}
    >
      <TabActiveProvider active={active}>{children}</TabActiveProvider>
    </div>
  );
}

/**
 * Sekmenin kendi yolunu çözer. Sonuç `useMemo` ile tutulur: sekme değişiminde tüm
 * panellerin yeniden eşleştirilmesi gereksiz iştir.
 *
 * YÖNLENDİRME ROTALARI RENDER EDİLMEZ: `/` ya da bilinmeyen bir yol `<Navigate>` döner;
 * GİZLİ bir sekmeden çalışırsa kullanıcının baktığı sayfayı kaçırırdı. Sekme deposu zaten
 * yalnız katalogdaki yolları açar — bu ikinci savunma.
 */
function TabRoute({ path }: { path: string }) {
  const el = useMemo(() => {
    const matches = matchRoutes(SHELL_ROUTES, path);
    if (!matches || matches.length === 0) return null;
    const last = matches[matches.length - 1]!;
    if (last.route.path === "*" || last.route.path === "/") return null;
    return renderMatches(matches);
  }, [path]);
  return <>{el}</>;
}
